import { useMemo } from 'react';

import { ContactGroupType } from 'contacts/enums';
import { getLocationRequestPayloadByAlternateVersion } from 'contacts/utils';
import { AlternateVersionHelper } from 'core/helpers';
import { CorporateUserType } from 'corporate/enums';
import { QuoteEntityType } from 'quotes/enums';
import { useQuoteActions } from 'quotes/hooks';
import { ICustomerQuote } from 'quotes/types';

import { ContactGroupAddressType, UpdateAddressByType } from './UpdateAddressByType';
import { IQuoteAddressType } from './UpdateAddressByType/types';

interface IProps {
  isOpen: boolean;
  quote?: ICustomerQuote;
  entityType?: QuoteEntityType;
  onClose: (updatedQuote?: ICustomerQuote) => void;
  addressType: ContactGroupAddressType;
  onSubmitSuccess?: () => void;
  isDefaultEditing?: boolean;
  hasCreateNewLocation?: boolean;
  hasCreateNewContact?: boolean;
  onChangeEmployees?: () => void;
}

export const UpdateQuoteAddress = ({
  isOpen,
  quote,
  entityType = QuoteEntityType.QUOTE,
  onClose,
  addressType,
  onSubmitSuccess,
  isDefaultEditing,
  hasCreateNewLocation,
  hasCreateNewContact,
  onChangeEmployees,
}: IProps) => {
  const { upsertCustomerQuoteAddress } = useQuoteActions();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ quote });

  const locationRequestPayload = useMemo(() => {
    const locationFilters = () => {
      switch (addressType) {
        case ContactGroupAddressType.SHIP_TO:
          return {};
        case ContactGroupAddressType.BILL_TO:
          return {
            ...(QuoteEntityType.ORDER === entityType && {
              alternateVersionEnabled: isAlternateVersionEnabled,
            }),
          };
        case ContactGroupAddressType.SOLD_TO:
          return { contactGroupId: quote?.customerContactGroup.id };
      }
    };

    return getLocationRequestPayloadByAlternateVersion(
      {
        filters: {
          projectId: quote?.vendorProjectId,
          contactGroupType: ContactGroupType.CUSTOMER,
          ...locationFilters(),
        },
      },
      quote?.customerContactGroup.id,
      isAlternateVersionEnabled
    );
  }, [
    addressType,
    entityType,
    isAlternateVersionEnabled,
    quote?.customerContactGroup.id,
    quote?.vendorProjectId,
  ]);

  const onSubmit = async (mappedData: IQuoteAddressType) => {
    const { success, quote: updatedQuote } = await upsertCustomerQuoteAddress({
      ...mappedData,
      quote: { id: quote?.id },
    });

    if (success && onSubmitSuccess) {
      onSubmitSuccess();
    }

    return {
      success,
      updatedParent: updatedQuote,
    };
  };

  return (
    <UpdateAddressByType<ICustomerQuote>
      key={addressType}
      isOpen={isOpen}
      parent={quote}
      onSubmit={onSubmit}
      onClose={onClose}
      type={addressType}
      customerContactGroup={quote?.customerContactGroup}
      locationRequestPayload={locationRequestPayload}
      contactRequestPayload={{
        filters: {
          userType: CorporateUserType.CUSTOMER,
        },
      }}
      isDefaultEditing={isDefaultEditing}
      hasCreateNewLocation={hasCreateNewLocation}
      hasCreateNewContact={hasCreateNewContact}
      onChangeEmployees={onChangeEmployees}
      isAlternateVersionEnabled={isAlternateVersionEnabled}
      disableArNumberFilterToggle={
        isAlternateVersionEnabled &&
        addressType === ContactGroupAddressType.BILL_TO &&
        QuoteEntityType.ORDER === entityType
      }
    />
  );
};
